import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Crown, ArrowRight } from "@phosphor-icons/react";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return "—";
  }
}

const fmtEur = (v) => v == null ? "—" : Number(v).toFixed(2).replace(".", ",") + " €";

/**
 * SubscriptionPanel — merchant's PayPal subscription status + history + cancel.
 * Also displays Premium / Premium AI pricing (promo + regular) so merchants
 * always know what they're paying (or would pay if upgrading).
 */
export default function SubscriptionPanel({ slug, isPremium }) {
  const [data, setData] = useState(null);
  const [plans, setPlans] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [interval] = useState("monthly"); // annual retired
  const [promoStatus, setPromoStatus] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/paypal/subscriptions/my?slug=${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/paypal/plans").then((r) => { setPlans(r.data.plans || {}); setPromoStatus(r.data.promo_status || null); }).catch(() => {}); }, []);

  const onCancel = async () => {
    if (!data?.subscription?.paypal_subscription_id) return;
    if (!window.confirm("Résilier votre abonnement Premium ? Vous conserverez les avantages jusqu'à la fin de la période en cours.")) return;
    setCancelling(true);
    try {
      await api.post(`/paypal/subscriptions/${data.subscription.paypal_subscription_id}/cancel`);
      toast.success("Abonnement résilié — vous resterez Premium jusqu'à la fin de la période.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
    setCancelling(false);
  };

  if (loading) return null;

  return (
    <div className="mt-8 bg-card border border-border rounded-sm p-6" data-testid="subscription-panel">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-sm bg-secondary/15 text-secondary grid place-items-center">
            <Crown size={18} weight="fill"/>
          </div>
          <div>
            <div className="font-display text-xl font-medium leading-tight">Mon abonnement</div>
            <div className="text-xs text-muted-foreground">Gérez votre plan, vos paiements, votre résiliation.</div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Plan actuel</div>
          <div className="font-display text-lg font-medium" data-testid="subscription-tier">
            {isPremium ? (data?.tier === "premium_ai" ? "Premium IA" : "Premium") : "Free"}
          </div>
        </div>
      </div>

      {!isPremium ? (
        <div data-testid="subscription-upgrade">
          <div className="mb-3">
            <div className="text-xs text-muted-foreground">
              Passez Premium pour débloquer tous les avantages (flyer PDF, rapport mensuel, IA, visibilité x2).
            </div>
          </div>

          {/* Price cards side by side */}
          <div className="grid sm:grid-cols-2 gap-3">
            {["premium", "premium_ai"].map((tier) => {
              const isPromoActive = promoStatus?.is_promo_active ?? true;
              const variant = isPromoActive ? "promo" : "standard";
              const p = plans?.[`${tier}_monthly_${variant}`];
              const per = "/ mois";
              const isHot = tier === "premium_ai";
              return (
                <div key={tier} className={`relative border rounded-sm p-4 ${isHot ? "border-primary bg-primary/5" : "border-border bg-accent/30"}`} data-testid={`subscription-upsell-${tier}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div className="font-display text-base font-medium">{tier === "premium_ai" ? "Premium IA" : "Premium"}</div>
                    {isHot && <span className="text-[9px] uppercase tracking-widest font-bold text-primary">Populaire</span>}
                  </div>
                  {isPromoActive ? (
                    <>
                      <div className="flex items-baseline gap-1.5 mb-1">
                        <span className="font-display text-2xl font-medium text-secondary tabular-nums" data-testid={`subscription-upsell-${tier}-price`}>{fmtEur(p?.promo_price)}</span>
                        <span className="text-[11px] text-muted-foreground">{per}</span>
                      </div>
                      <div className="text-[10px] font-bold uppercase tracking-wider text-secondary mb-1">
                        -50 % les 12 premiers mois
                      </div>
                      <div className="text-[11px] text-muted-foreground">
                        Puis <b className="text-foreground">{fmtEur(p?.regular_price)}</b> {per}
                      </div>
                    </>
                  ) : (
                    <div className="flex items-baseline gap-1.5">
                      <span className="font-display text-2xl font-medium tabular-nums" data-testid={`subscription-upsell-${tier}-price`}>{fmtEur(p?.regular_price)}</span>
                      <span className="text-[11px] text-muted-foreground">{per}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <a href="/passez-partenaire" className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-secondary text-secondary-foreground hover:bg-[#b56847] hover:text-white px-4 py-2.5 text-sm font-semibold rounded-sm transition-colors" data-testid="subscription-upgrade-cta">
            Passer Premium <ArrowRight size={14}/>
          </a>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Current plan pricing summary */}
          {(() => {
            const currentTier = data?.tier === "premium_ai" ? "premium_ai" : "premium";
            const subInterval = data?.subscription?.interval === "annual" ? "annual" : "monthly";
            const subVariant = data?.subscription?.is_promo === false ? "standard" : "promo";
            const p = plans?.[`${currentTier}_${subInterval}_${subVariant}`];
            const per = subInterval === "annual" ? "/ an" : "/ mois";
            const trial = data?.subscription?.trial || data?.premium?.trial;
            return (
              <div className="bg-primary/5 border border-primary/30 rounded-sm p-4 flex items-center justify-between gap-4 flex-wrap" data-testid="subscription-current-price">
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-primary mb-1">
                    {trial ? "Essai gratuit en cours" : `Votre abonnement ${subInterval === "annual" ? "annuel" : "mensuel"}`}
                  </div>
                  {trial ? (
                    <div className="text-sm">
                      <b className="font-semibold">Gratuit</b> jusqu'au {formatDate(data?.subscription?.expires_at || data?.premium?.expires_at)}.
                      <div className="text-xs text-muted-foreground mt-0.5">Ensuite : {fmtEur(p?.regular_price)} {per}</div>
                    </div>
                  ) : (
                    <div className="flex items-baseline gap-1.5">
                      <span className="font-display text-2xl font-medium tabular-nums" data-testid="subscription-current-amount">{fmtEur(p?.regular_price)}</span>
                      <span className="text-xs text-muted-foreground">{per}</span>
                    </div>
                  )}
                </div>
                <a href="/passez-partenaire" className="text-xs font-semibold text-primary hover:underline">
                  Voir les offres →
                </a>
              </div>
            );
          })()}

          <div className="grid sm:grid-cols-3 gap-3 text-sm">
            <div className="bg-accent/20 border border-border rounded-sm p-3">
              <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Statut</div>
              <div className="font-semibold mt-0.5" data-testid="subscription-status">{data?.is_active ? "Actif" : (data?.subscription?.status || "Inconnu")}</div>
            </div>
            <div className="bg-accent/20 border border-border rounded-sm p-3">
              <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Depuis</div>
              <div className="font-semibold mt-0.5">{formatDate(data?.subscription?.created_at)}</div>
            </div>
            <div className="bg-accent/20 border border-border rounded-sm p-3">
              <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Paiements</div>
              <div className="font-semibold mt-0.5" data-testid="subscription-payments-count">{data?.payments?.length || 0}</div>
            </div>
          </div>

          {data?.payments && data.payments.length > 0 && (
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Historique (6 derniers)</div>
              <ul className="text-sm space-y-1" data-testid="subscription-payments-list">
                {data.payments.map((p, i) => (
                  <li key={p.id || `${p.at}-${i}`} className="flex items-center justify-between border-b border-border/60 py-1.5">
                    <span className="text-muted-foreground">{formatDate(p.at)}</span>
                    <span className="font-semibold tabular-nums">{p.amount} {p.currency}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {data?.is_active && data?.subscription?.paypal_subscription_id && (
            <div className="flex items-center justify-end pt-2 border-t border-border">
              <button
                onClick={onCancel}
                disabled={cancelling}
                className="text-xs font-semibold text-secondary hover:text-[#b56847] disabled:opacity-50"
                data-testid="subscription-cancel-btn"
              >
                {cancelling ? "Résiliation…" : "Résilier mon abonnement"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
